import { useEffect, useState } from 'react';
import { supabase, isSupabaseEnabled } from './supabaseClient';

// Price Impact model for the Agoracle Wards.
//
// The server gives us a "base" price per outcome (derived from the
// Oracle's LMSR / Dynamic Consensus Balancing). We then nudge that
// price upward by a small amount per 100 units already pledged to the
// Ward across the whole Polis. This gives a visible "weight of
// conviction" feedback loop even while the Dummy Pot is running.
//
//   adjusted = min(MAX, base + (units / 100) × IMPACT_PER_100)
//
// Tune IMPACT_PER_100 up to make the market feel heavier. 0.005 means
// 100 units nudges the price by 0.5 percentage points; 1,000 units by
// 5pp. Capped so a runaway Ward can't cross the 1.0 certainty line.

export const IMPACT_PER_100     = 0.005;
export const MAX_IMPACT_PRICE   = 0.99;
export const SLIPPAGE_THRESHOLD = 200; // units — above this we warn

export interface OutcomeAggregate {
  units_total:  number;
  cost_total:   number;
  pledge_count: number;
}

export type OutcomeAggregates = Record<string, OutcomeAggregate>;

export function applyImpact(basePrice: number, totalUnits: number): number {
  const impact = (totalUnits / 100) * IMPACT_PER_100;
  return Math.min(MAX_IMPACT_PRICE, Math.max(0, basePrice + impact));
}

// Percentage shift between "price now" and "price if the user pledges N
// more units", given the current pre-pledge Polis total. Positive =
// the pledge drives the Ward price higher.
export function slippagePct(
  basePrice:         number,
  unitsBeforePledge: number,
  unitsIntended:     number,
): number {
  const before = applyImpact(basePrice, unitsBeforePledge);
  const after  = applyImpact(basePrice, unitsBeforePledge + unitsIntended);
  if (before <= 0) return 0;
  return ((after - before) / before) * 100;
}

// Fetches pledge aggregates from Supabase, keyed by outcome_name.
// Primary signal is Realtime (postgres_changes on public.pledges) —
// the poll is a long-interval fallback for when the socket drops or
// Realtime hasn't been enabled on the table. Returns an empty map when
// Supabase isn't wired so callers can render without a null-guard.
//
// **I-006 hazard (global form):** this hook aggregates pledges across
// all markets, keyed by outcome_name only. Two markets that share an
// outcome label (e.g. both have a "YES") will collide. For per-market
// reads (cockpit pledge form, outcome row), prefer
// `useMarketOutcomeAggregates(marketId)` below. This unfiltered form
// is retained for global-leaderboard-style callers that genuinely
// want the cross-market roll-up.
export function useOutcomeAggregates(pollMs = 30000): OutcomeAggregates {
  const [agg, setAgg] = useState<OutcomeAggregates>({});

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!isSupabaseEnabled() || !supabase) return;
      const { data, error } = await supabase
        .from('pledges')
        .select('outcome_name, units, total_cost');
      if (error || !data || cancelled) return;

      const next: OutcomeAggregates = {};
      for (const p of data as Array<{ outcome_name: string; units: number | string; total_cost: number | string }>) {
        const key = p.outcome_name;
        if (!key) continue;
        const row = next[key] ?? { units_total: 0, cost_total: 0, pledge_count: 0 };
        row.units_total  += Number(p.units)      || 0;
        row.cost_total   += Number(p.total_cost) || 0;
        row.pledge_count += 1;
        next[key] = row;
      }
      if (!cancelled) setAgg(next);
    }

    load();
    const t = setInterval(load, pollMs);

    // Realtime: any INSERT/UPDATE/DELETE on pledges triggers a full
    // reload. The query is cheap (three columns, table-scan) and
    // recomputing the map in one place keeps the aggregation logic
    // honest. If Realtime isn't enabled on the table the subscription
    // silently does nothing and the interval keeps us fresh.
    const channel = supabase
      ?.channel('agoracle:pledges:aggregates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pledges' },
        () => { if (!cancelled) load(); },
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(t);
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [pollMs]);

  return agg;
}

// Whether a market has at least one operator-seed pledge row
// (`is_operator_seed = TRUE`). Returns:
//   - `undefined` while the first probe is in flight
//   - `false` when the market is genuinely unseeded
//   - `true` once a seed row is observed
//
// Drives the cockpit's unseeded-market guard — the soft-fail banner
// that refuses pledges on a market the operator has not yet seeded.
// Seed-state is rare to change (operator seeds once at market launch)
// so polling on the long fallback interval is sufficient; Realtime
// catches the live seeding case.
export function useMarketHasOperatorSeed(
  marketId: string | undefined,
  pollMs   = 60000,
): boolean | undefined {
  const [seeded, setSeeded] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!marketId) {
      setSeeded(undefined);
      return;
    }
    let cancelled = false;

    async function probe(): Promise<void> {
      if (!isSupabaseEnabled() || !supabase) {
        if (!cancelled) setSeeded(true); // Fail-open: don't block when Supabase is offline.
        return;
      }
      const { data, error } = await supabase
        .from('pledges')
        .select('id')
        .eq('market_id', marketId)
        .eq('is_operator_seed', true)
        .limit(1);
      if (cancelled) return;
      if (error) {
        setSeeded(true); // Fail-open on query error so the cockpit doesn't lock up on a transient RLS hiccup.
        return;
      }
      setSeeded(Boolean(data && data.length > 0));
    }

    probe();
    const t = setInterval(probe, pollMs);

    const channel = supabase
      ?.channel(`agoracle:pledges:seed-marker:${marketId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'pledges', filter: `market_id=eq.${marketId}` },
        () => { if (!cancelled) probe(); },
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(t);
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [marketId, pollMs]);

  return seeded;
}

// All-markets pledge aggregates, double-keyed by `market_id` then
// `outcome_name`. This is the I-006-safe form of `useOutcomeAggregates`
// for callers that render a *list* of markets and so can't call a
// per-market hook (rules of hooks) — typically the feed surface. The
// returned shape is a nested map: `agg[marketId]?.[outcomeName]`
// returns the OutcomeAggregate for that pair, or undefined if no
// pledges exist yet. Markets that share an outcome label (e.g. "The
// Field", "Hold at 3.75%", "Yes"/"No") get separate buckets — that's
// the entire point.
//
// Why this exists alongside `useOutcomeAggregates`: the bare-label
// global form is genuinely correct for cross-market roll-ups
// (leaderboards, "total Yes pledged across the Arena" kinds of
// questions). The feed is NOT that caller — it needs each row's own
// pool / favourite / outsider, and the bare-label form contaminates
// every row with cross-market totals (the I-006 hazard the global
// hook's own doc comment warns about).
export type MarketScopedOutcomeAggregates = Record<string, OutcomeAggregates>;

export function useAllMarketsOutcomeAggregates(pollMs = 30000): MarketScopedOutcomeAggregates {
  const [agg, setAgg] = useState<MarketScopedOutcomeAggregates>({});

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!isSupabaseEnabled() || !supabase) return;
      const { data, error } = await supabase
        .from('pledges')
        .select('market_id, outcome_name, units, total_cost');
      if (error || !data || cancelled) return;

      const next: MarketScopedOutcomeAggregates = {};
      for (const p of data as Array<{
        market_id:    string;
        outcome_name: string;
        units:        number | string;
        total_cost:   number | string;
      }>) {
        const mid   = p.market_id;
        const label = p.outcome_name;
        if (!mid || !label) continue;
        const perMarket = next[mid] ?? {};
        const row = perMarket[label] ?? { units_total: 0, cost_total: 0, pledge_count: 0 };
        row.units_total  += Number(p.units)      || 0;
        row.cost_total   += Number(p.total_cost) || 0;
        row.pledge_count += 1;
        perMarket[label] = row;
        next[mid] = perMarket;
      }
      if (!cancelled) setAgg(next);
    }

    load();
    const t = setInterval(load, pollMs);

    // Realtime: any pledge change anywhere triggers a full reload.
    // The query is cheap (four columns) and re-aggregating in one
    // place keeps the keying honest — there's no chance of half-
    // updating a market_id bucket.
    const channel = supabase
      ?.channel('agoracle:pledges:aggregates:all-markets')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pledges' },
        () => { if (!cancelled) load(); },
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(t);
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [pollMs]);

  return agg;
}

// Per-market pledge aggregates, keyed by outcome_name. Same shape as
// `useOutcomeAggregates` but filtered by `market_id` at both the
// initial fetch and the Realtime subscription, closing the I-006
// collision risk for callers that already have a market in scope
// (cockpit outcome row, pledge form). When `marketId` is undefined
// the hook returns an empty map without firing any network calls.
export function useMarketOutcomeAggregates(
  marketId:  string | undefined,
  pollMs   = 30000,
): OutcomeAggregates {
  const [agg, setAgg] = useState<OutcomeAggregates>({});

  useEffect(() => {
    if (!marketId) {
      setAgg({});
      return;
    }
    let cancelled = false;

    async function load(): Promise<void> {
      if (!isSupabaseEnabled() || !supabase) return;
      const { data, error } = await supabase
        .from('pledges')
        .select('outcome_name, units, total_cost')
        .eq('market_id', marketId);
      if (error || !data || cancelled) return;

      const next: OutcomeAggregates = {};
      for (const p of data as Array<{ outcome_name: string; units: number | string; total_cost: number | string }>) {
        const key = p.outcome_name;
        if (!key) continue;
        const row = next[key] ?? { units_total: 0, cost_total: 0, pledge_count: 0 };
        row.units_total  += Number(p.units)      || 0;
        row.cost_total   += Number(p.total_cost) || 0;
        row.pledge_count += 1;
        next[key] = row;
      }
      if (!cancelled) setAgg(next);
    }

    load();
    const t = setInterval(load, pollMs);

    // Filtered Realtime subscription — only pledges for this market
    // wake the cockpit. Saves a re-aggregation on every unrelated
    // pledge across the Arena.
    const channel = supabase
      ?.channel(`agoracle:pledges:aggregates:${marketId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pledges', filter: `market_id=eq.${marketId}` },
        () => { if (!cancelled) load(); },
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(t);
      if (channel && supabase) supabase.removeChannel(channel);
    };
  }, [marketId, pollMs]);

  return agg;
}
